import { useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, KeyRound } from "lucide-react";
import { api, apiError } from "@/lib/api";
import PasswordInput from "@/components/PasswordInput";
import { toast } from "sonner";

export default function ChangePasswordModal({ open, onClose }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [cf, setCf] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const reset = () => {
    setCur("");
    setNw("");
    setCf("");
    setErr("");
  };
  const close = () => {
    reset();
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (nw.length < 8) {
      setErr("New password must be at least 8 characters.");
      return;
    }
    if (nw !== cf) {
      setErr("New passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/me/password", { current_password: cur, new_password: nw });
      toast.success("Password updated successfully");
      close();
    } catch (e2) {
      setErr(apiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100]">
      <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={close} />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="min-h-full flex items-center justify-center p-4">
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 my-4" data-testid="change-password-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="grid place-items-center h-9 w-9 rounded-lg bg-indigo-50 text-indigo-600">
                  <KeyRound className="h-4 w-4" />
                </div>
                <h3 className="font-head text-lg font-bold text-slate-900">Change Password</h3>
              </div>
              <button onClick={close} className="text-slate-400 hover:text-slate-700" data-testid="change-password-close">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={submit} className="px-6 py-5 space-y-4" data-testid="change-password-form">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Current Password</label>
                <PasswordInput className="h-11" value={cur} onChange={setCur} testid="cp-current" autoComplete="current-password" required />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">New Password</label>
                <PasswordInput className="h-11" value={nw} onChange={setNw} testid="cp-new" autoComplete="new-password" placeholder="At least 8 characters" required />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1.5">Confirm New Password</label>
                <PasswordInput className="h-11" value={cf} onChange={setCf} testid="cp-confirm" autoComplete="new-password" required />
              </div>

              {err && (
                <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="cp-error">
                  {err}
                </p>
              )}

              <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={close} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                  Cancel
                </button>
                <button type="submit" disabled={loading} data-testid="cp-submit" className="h-10 px-5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2">
                  {loading && <Loader2 className="h-4 w-4 animate-spin" />} Update Password
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
